/**
 * Taking the analyst's decision on something the agent is waiting for — a
 * query to run, a call to place — for every chat.
 *
 * Each function updates the chat for the decision and returns what to send
 * back to resume the turn, or null when nothing is waiting. Sending it is the
 * caller's job, since each place talks to the server with its own context.
 */
import { newMessageId, type ChatGet } from './processChatEvent';

export type ApprovalAction = 'approve' | 'edit' | 'reject';

export interface ResumeData {
  type: 'sql_approval' | 'voice_call_approval';
  /** Left out of the request when the approval arrived without one, as before. */
  session_id?: string;
  response: Record<string, unknown>;
}

export function decideSqlApproval(
  get: ChatGet,
  action: ApprovalAction,
  editedSql?: string,
  reason?: string,
): ResumeData | null {
  const store = get();
  const approval = store.pendingSQLApproval;
  if (!approval || !store.threadId) return null;

  store.setPendingSQLApproval(null);
  store.setIsStreaming(true);
  // The step that asked for approval is finished the moment it is answered.
  // Without a step saying so, the list keeps "Requesting approval…" as the
  // one in progress, and the turn reads as still waiting on the analyst.
  if (action === 'reject') {
    store.appendStreamingStatus('Rejected — not running the query.');
    store.setStreamingAction('Processing your feedback…');
    const ackId = newMessageId(get, 'system-ack');
    store.addMessage({
      id: ackId,
      role: 'assistant',
      content: reason
        ? `Noted - "${reason}". Let me adjust.`
        : 'Query rejected. Let me adjust.',
      timestamp: new Date().toISOString(),
    });
    store.appendTimelineAssistantMessage(ackId);
  } else if (action === 'edit') {
    store.appendStreamingStatus('Approved with your edits — running the query.');
    store.setStreamingAction('Running edited query…');
  } else {
    store.appendStreamingStatus('Approved — running the query.');
    store.setStreamingAction('Running query…');
  }

  return {
    type: 'sql_approval',
    session_id: approval.sessionId,
    response: {
      action,
      ...(action === 'edit' && editedSql ? { edited_sql: editedSql } : {}),
      ...(action === 'reject' && reason ? { reason } : {}),
    },
  };
}

export function decideVoiceCallApproval(
  get: ChatGet,
  action: ApprovalAction,
  editedNumber?: string,
  reason?: string,
): ResumeData | null {
  const store = get();
  const approval = store.pendingVoiceCallApproval;
  if (!approval || !store.threadId) return null;

  store.setPendingVoiceCallApproval(null);
  store.setIsStreaming(true);

  if (action === 'reject') {
    store.appendStreamingStatus('Not placing the call.');
    store.setStreamingAction('Noted — not calling…');
    const ackId = newMessageId(get, 'system-ack');
    store.addMessage({
      id: ackId,
      role: 'assistant',
      content: 'Understood — I won\'t place that call.',
      timestamp: new Date().toISOString(),
    });
    store.appendTimelineAssistantMessage(ackId);
  } else {
    store.appendStreamingStatus('Approved — placing the call.');
    store.setStreamingAction('On a call…');
  }

  return {
    type: 'voice_call_approval',
    session_id: approval.sessionId,
    response: {
      action,
      ...(action === 'edit' && editedNumber ? { edited_number: editedNumber } : {}),
      ...(action === 'reject' && reason ? { reason } : {}),
    },
  };
}
